import React, { useEffect, useRef, useState } from 'react';
import Axios from 'axios';
import Icon from '@/components/ui/Icon';
import Text from '@/components/ui/Text';
import { Button, SubmitButton } from '@/components/ui/Buttons';
import setupCsrfToken from '@/modules/SetupCSRFToken';
import SelectSubscriberModal from './SelectSubscriberModal';

type QtiStatus = 'pending' | 'processing' | 'completed' | 'failed';
type Phase = 'generating' | 'ready' | 'failed' | 'stalled';

interface QtiDownloadButtonProps {
  buildPackageUrl: string;
  createDownloadUrl?: string;
  downloadUrl?: string;
  isUpdatedUrl: string;
  packageId: number;
  requireSubscriberSelection?: boolean;
  status: QtiStatus;
}

const POLL_INTERVAL_MS = 3000;
const MAX_ATTEMPTS = 100; // ~5 min — generous headroom for the largest programs

const initialPhase = (status: QtiStatus): Phase => {
  if (status === 'completed') return 'ready';
  if (status === 'failed') return 'failed';
  return 'generating';
};

// Download without navigating the current page, so the table view stays put.
const triggerDownload = (url: string) => {
  const link = document.createElement('a');
  link.href = url;
  document.body.appendChild(link);
  link.click();
  link.remove();
};

const QtiDownloadButton = ({
  buildPackageUrl,
  createDownloadUrl,
  downloadUrl,
  isUpdatedUrl,
  packageId,
  requireSubscriberSelection = false,
  status,
}: QtiDownloadButtonProps) => {
  const [phase, setPhase] = useState<Phase>(() => initialPhase(status));
  const [url, setUrl] = useState<string | undefined>(downloadUrl);
  const [subscriberModalOpen, setSubscriberModalOpen] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout>>();

  // AIDEV-NOTE: the "Last Updated" cell is a sibling ERB <td> outside this per-cell React island,
  // so we write to it directly (restores the legacy jQuery behavior) when generation resolves.
  const writeTimestamp = (value?: string) => {
    if (!value) return;
    const cell = document.querySelector(`.package_updated_at_${packageId}`);
    if (cell) cell.textContent = value;
  };

  const poll = (attempt: number) => {
    if (attempt > MAX_ATTEMPTS) {
      setPhase('stalled');
      return;
    }

    Axios.get(isUpdatedUrl)
      .then(({ data }) => {
        if (data.status === 'completed' && data.url) {
          setUrl(data.url);
          writeTimestamp(data.updated_at);
          setPhase('ready');
        } else if (data.status === 'failed' || data.status === 'completed') {
          // A 'completed' job with no URL is a failure, not a reason to keep polling.
          writeTimestamp(data.updated_at);
          setPhase('failed');
        } else {
          timer.current = setTimeout(() => poll(attempt + 1), POLL_INTERVAL_MS);
        }
      })
      .catch(() => setPhase('failed'));
  };

  const regenerate = () => {
    setupCsrfToken();
    setPhase('generating');
    Axios.post(buildPackageUrl)
      .then(() => poll(1))
      .catch(() => setPhase('failed'));
  };

  const checkAgain = () => {
    setPhase('generating');
    poll(1);
  };

  useEffect(() => {
    if (phase === 'generating') poll(1);
    return () => clearTimeout(timer.current);
  }, []);

  return (
    <div aria-live="polite" role="status">
      {phase === 'generating' && (
        <SubmitButton submitting submittingText="Generating…" />
      )}

      {phase === 'ready' && (
        <span className="tw-flex tw-items-center tw-gap-3">
          {url && (
            <Button
              onClick={() => (
                requireSubscriberSelection && createDownloadUrl
                  ? setSubscriberModalOpen(true)
                  : triggerDownload(url)
              )}
            >
              Download
            </Button>
          )}
          <Button outlineStyle small onClick={regenerate}>
            Regenerate
          </Button>
          {url && createDownloadUrl && subscriberModalOpen && (
            <SelectSubscriberModal
              isOpen
              close={() => setSubscriberModalOpen(false)}
              createDownloadUrl={createDownloadUrl}
              packageId={packageId}
              onDownload={() => triggerDownload(url)}
            />
          )}
        </span>
      )}

      {phase === 'failed' && (
        <span className="tw-flex tw-items-center tw-gap-3">
          <Button onClick={regenerate}>
            <span className="tw-flex tw-items-center tw-gap-2">
              <Icon fill="#fff" icon="Refresh" size={16} />
              Retry
            </span>
          </Button>
          <Text className="tw-text-red" size="sm">
            Something went wrong
          </Text>
        </span>
      )}

      {phase === 'stalled' && (
        <span className="tw-flex tw-items-center tw-gap-3">
          <Text className="tw-text-very-dark-grey" size="sm">
            Still working…
          </Text>
          <Button outlineStyle small onClick={checkAgain}>
            Check again
          </Button>
        </span>
      )}
    </div>
  );
};

export default QtiDownloadButton;
